import { useState, useEffect } from "react";
import {
  CreditCard, Download, Check, AlertTriangle, TrendingUp,
  Users, HardDrive, BookOpen, Activity, ChevronRight, FileText,
  Loader2, ArrowUpRight, Calendar, Eye, Star, Zap,
  Shield, Headphones, Globe, BarChart3, Lock, HelpCircle
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelpTip = ({ title, children }) => (
  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2.5">
    <HelpCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
    <div><p className="text-xs font-semibold text-blue-800">{title}</p><p className="text-[11px] text-blue-700 mt-0.5 leading-relaxed">{children}</p></div>
  </div>
);

const BillingPage = () => {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [downloadingId, setDownloadingId] = useState(null);
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bRes, iRes] = await Promise.all([
          api.get("/billing/overview"),
          api.get("/billing/invoices"),
        ]);
        setBilling(bRes.data);
        setInvoices(iRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleDownloadPdf = async (invoiceId) => {
    setDownloadingId(invoiceId);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (e) {
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewPdf = async (invoiceId) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to load");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Failed to view invoice");
    }
  };

  if (loading) return (
    <AdminLayout title="Billing & Plans" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  const b = billing;
  const usageItems = b ? [
    { label: "Active Users", icon: Users, current: b.usage.users.current, limit: b.usage.users.limit, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Storage", icon: HardDrive, current: b.usage.storage_gb.current, limit: b.usage.storage_gb.limit, unit: "GB", color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Courses", icon: BookOpen, current: b.usage.courses.current, limit: b.usage.courses.limit, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "API Calls", icon: Activity, current: b.usage.api_calls.current, limit: b.usage.api_calls.limit, color: "text-amber-600", bg: "bg-amber-50" },
  ] : [];

  const plans = [
    {
      name: "Starter", price: { monthly: 49, annual: 39 }, tier: "starter",
      desc: "For small teams getting started with training",
      features: [
        { text: "Up to 25 users", included: true },
        { text: "5 GB storage", included: true },
        { text: "Core LMS features", included: true },
        { text: "Email support", included: true },
        { text: "Basic analytics", included: true },
        { text: "SCORM 1.2 support", included: true },
        { text: "Custom branding", included: false },
        { text: "API access", included: false },
        { text: "SSO/SAML", included: false },
      ],
      current: b?.plan?.name === "Starter",
      cta: "Get Started",
    },
    {
      name: "Professional", price: { monthly: 299, annual: 249 }, tier: "professional",
      desc: "For growing organizations with compliance needs",
      features: [
        { text: "Up to 200 users", included: true },
        { text: "100 GB storage", included: true },
        { text: "Full course library", included: true },
        { text: "Priority support", included: true },
        { text: "Advanced analytics", included: true },
        { text: "SCORM 1.2 & 2004", included: true },
        { text: "Custom branding", included: true },
        { text: "API access", included: true },
        { text: "SSO/SAML", included: false },
      ],
      current: b?.plan?.name === "Professional",
      popular: true,
      cta: "Upgrade Now",
    },
    {
      name: "Enterprise", price: null, tier: "enterprise",
      desc: "For large organizations with advanced security needs",
      features: [
        { text: "Unlimited users", included: true },
        { text: "Unlimited storage", included: true },
        { text: "Custom course development", included: true },
        { text: "Dedicated CSM", included: true },
        { text: "Executive analytics", included: true },
        { text: "All SCORM standards", included: true },
        { text: "White labeling", included: true },
        { text: "Full API access", included: true },
        { text: "SSO/SAML + SCIM", included: true },
      ],
      current: b?.plan?.name === "Enterprise",
      cta: "Contact Sales",
    },
  ];

  const featureComparison = [
    { category: "Users & Storage", features: [
      { name: "User Seats", starter: "25", professional: "200", enterprise: "Unlimited" },
      { name: "Storage", starter: "5 GB", professional: "100 GB", enterprise: "Unlimited" },
      { name: "Course Limit", starter: "50", professional: "500", enterprise: "Unlimited" },
    ]},
    { category: "Learning", features: [
      { name: "Course Builder", starter: true, professional: true, enterprise: true },
      { name: "SCORM Support", starter: "1.2", professional: "1.2 & 2004", enterprise: "All Standards" },
      { name: "Learning Paths", starter: false, professional: true, enterprise: true },
      { name: "Assessments", starter: true, professional: true, enterprise: true },
      { name: "ILT Sessions", starter: false, professional: true, enterprise: true },
    ]},
    { category: "Compliance", features: [
      { name: "Compliance Tracking", starter: "Basic", professional: "Advanced", enterprise: "Advanced+" },
      { name: "Auto-Assignments", starter: false, professional: true, enterprise: true },
      { name: "Audit Logs", starter: false, professional: true, enterprise: true },
      { name: "Certificate Management", starter: true, professional: true, enterprise: true },
    ]},
    { category: "Support & Security", features: [
      { name: "Support Level", starter: "Email", professional: "Priority", enterprise: "Dedicated CSM" },
      { name: "SSO/SAML", starter: false, professional: false, enterprise: true },
      { name: "Custom Branding", starter: false, professional: true, enterprise: true },
      { name: "API Access", starter: false, professional: true, enterprise: true },
      { name: "SLA Guarantee", starter: false, professional: false, enterprise: true },
    ]},
  ];

  const renderFeatureValue = (val) => {
    if (val === true) return <Check className="w-4 h-4 text-emerald-500 mx-auto" />;
    if (val === false) return <span className="text-muted-foreground/40 text-xs">--</span>;
    return <span className="text-xs font-medium">{val}</span>;
  };

  return (
    <AdminLayout title="Billing & Plans" variant={variant}>
      <div className="space-y-5 max-w-6xl" data-testid="billing-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/50 h-9">
            <TabsTrigger value="overview" className="text-xs gap-1.5" data-testid="tab-overview">
              <CreditCard className="w-3.5 h-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs gap-1.5" data-testid="tab-invoices">
              <FileText className="w-3.5 h-3.5" /> Invoices
              <Badge variant="secondary" className="ml-1 text-[9px] px-1.5 py-0 h-4">{invoices.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="plans" className="text-xs gap-1.5" data-testid="tab-plans">
              <TrendingUp className="w-3.5 h-3.5" /> Plans & Pricing
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === "overview" && b && (
          <div className="space-y-5">
            <HelpTip title="Billing Overview">
              View your current plan details, usage metrics, and payment information. Upgrade your plan or update payment methods at any time.
            </HelpTip>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border shadow-none lg:col-span-2">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold" data-testid="plan-name">{b.plan.name} Plan</h2>
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-xs capitalize">{b.plan.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Started {new Date(b.plan.started_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black text-primary">${b.plan.price}</p>
                      <p className="text-[10px] text-muted-foreground">per {b.plan.interval}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Next billing date</p>
                      <p className="text-xs text-muted-foreground">{new Date(b.plan.next_billing_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <Button variant="outline" size="sm" className="ml-auto text-xs h-8" onClick={() => setActiveTab("plans")}>
                      Change Plan <ArrowUpRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card className="border shadow-none">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary" /> Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-4 space-y-3">
                  <div className="p-3 rounded-lg border flex items-center gap-3">
                    <div className="w-10 h-7 rounded bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                      <span className="text-white text-[8px] font-bold tracking-wider">VISA</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">**** **** **** {b.payment_method.last4}</p>
                      <p className="text-[10px] text-muted-foreground">Expires {b.payment_method.exp_month}/{b.payment_method.exp_year}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full text-xs h-8" onClick={() => toast.info("Stripe integration coming soon")} data-testid="update-payment-btn">
                    Update Payment Method
                  </Button>
                  <div className="pt-1">
                    <p className="text-[10px] text-muted-foreground">Billing contact</p>
                    <p className="text-xs font-medium">{b.billing_contact.name}</p>
                    <p className="text-[10px] text-muted-foreground">{b.billing_contact.email}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border shadow-none">
              <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm">Usage This Period</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {usageItems.map((u, i) => {
                    const pct = Math.round((u.current / u.limit) * 100);
                    const isHigh = pct > 80;
                    return (
                      <div key={i} className="space-y-2 p-3 rounded-lg border" data-testid={`usage-${u.label.toLowerCase().replace(/\s/g,'-')}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg ${u.bg} flex items-center justify-center`}>
                            <u.icon className={`w-4 h-4 ${u.color}`} />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">{u.label}</p>
                            <p className="text-sm font-bold">{u.current.toLocaleString()}{u.unit ? ` ${u.unit}` : ""}</p>
                          </div>
                        </div>
                        <Progress value={pct} className={`h-1.5 ${isHigh ? "[&>div]:bg-red-500" : ""}`} />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">{pct}% of {u.limit.toLocaleString()}{u.unit ? ` ${u.unit}` : ""}</span>
                          {isHigh && <Badge className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0">Near limit</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="border shadow-none">
              <CardHeader className="pb-2 pt-4 px-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Recent Invoices</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setActiveTab("invoices")}>
                  View All <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="space-y-2">
                  {invoices.slice(0, 3).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/20 transition-colors" data-testid={`invoice-${inv.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{inv.id}</p>
                          <p className="text-[10px] text-muted-foreground">{inv.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold">${inv.amount.toFixed(2)}</span>
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">{inv.status}</Badge>
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => handleViewPdf(inv.id)} data-testid={`view-pdf-${inv.id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── INVOICES TAB ── */}
        {activeTab === "invoices" && (
          <div className="space-y-4">
            <HelpTip title="Invoice Management">
              Download invoices as PDF for your records or view them directly in the browser. All invoices are stored for 7 years per compliance requirements.
            </HelpTip>
            <Card className="border shadow-none" data-testid="invoices-table">
              <CardHeader className="pb-2 pt-4 px-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Invoice History</CardTitle>
                <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={() => toast.success("CSV export coming soon")}>
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-12 gap-2 text-[10px] text-muted-foreground uppercase tracking-wider font-semibold px-5 py-3 border-y bg-muted/30">
                  <span className="col-span-2">Invoice</span><span className="col-span-2">Date</span>
                  <span className="col-span-3">Description</span><span className="col-span-1 text-right">Amount</span>
                  <span className="col-span-1 text-center">Status</span><span className="col-span-3 text-center">Actions</span>
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                  {invoices.map(inv => (
                    <div key={inv.id} className="grid grid-cols-12 gap-2 items-center px-5 py-3 border-b hover:bg-muted/20 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <span className="col-span-2 text-sm font-medium">{inv.id}</span>
                      <span className="col-span-2 text-sm text-muted-foreground">{new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="col-span-3 text-sm">{inv.description}</span>
                      <span className="col-span-1 text-sm font-bold text-right">${inv.amount.toFixed(2)}</span>
                      <span className="col-span-1 text-center">
                        <Badge className={`text-[10px] px-1.5 py-0 ${inv.status === "paid" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"} border`}>{inv.status}</Badge>
                      </span>
                      <span className="col-span-3 flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-primary" onClick={() => handleViewPdf(inv.id)} data-testid={`view-btn-${inv.id}`}>
                          <Eye className="w-3 h-3" /> View
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => handleDownloadPdf(inv.id)} disabled={downloadingId === inv.id} data-testid={`download-btn-${inv.id}`}>
                          {downloadingId === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 border-t bg-muted/20 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{invoices.length} invoices</span>
                  <span className="text-sm font-bold">Total: ${invoices.reduce((s, i) => s + i.amount, 0).toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── PLANS TAB — HubSpot-style ── */}
        {activeTab === "plans" && (
          <div className="space-y-6">
            <HelpTip title="Plans & Pricing">
              Choose the plan that best fits your organization. All plans include core LMS features, compliance tracking, and certificate management. Upgrade or downgrade at any time.
            </HelpTip>

            {/* Billing cycle toggle */}
            <div className="flex items-center justify-center gap-3">
              <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
              <button
                onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
                className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === "annual" ? "bg-primary" : "bg-muted-foreground/30"}`}
                data-testid="billing-cycle-toggle"
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billingCycle === "annual" ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm font-medium ${billingCycle === "annual" ? "text-foreground" : "text-muted-foreground"}`}>Annual</span>
              {billingCycle === "annual" && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Save 20%</Badge>}
            </div>

            {/* Plan Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {plans.map((plan) => (
                <Card
                  key={plan.name}
                  className={`border shadow-none relative transition-all hover:shadow-md ${
                    plan.current ? "border-primary border-2 ring-2 ring-primary/10" :
                    plan.popular ? "border-primary/40" : ""
                  }`}
                  data-testid={`plan-card-${plan.name.toLowerCase()}`}
                >
                  {plan.popular && !plan.current && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground text-xs px-3 py-0.5 gap-1 shadow-sm">
                        <Star className="w-3 h-3" /> Most Popular
                      </Badge>
                    </div>
                  )}
                  {plan.current && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-green-600 text-white text-xs px-3 py-0.5 gap-1 shadow-sm">
                        <Check className="w-3 h-3" /> Current Plan
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-6 pt-7">
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{plan.desc}</p>
                    <div className="mt-4 mb-5">
                      {plan.price ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-black tracking-tight">${plan.price[billingCycle]}</span>
                          <span className="text-sm text-muted-foreground">/ user / mo</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-3xl font-black">Custom</span>
                          <p className="text-xs text-muted-foreground mt-1">Tailored to your needs</p>
                        </div>
                      )}
                      {plan.price && billingCycle === "annual" && (
                        <p className="text-[11px] text-green-600 font-medium mt-1">
                          Billed ${plan.price.annual * 12}/year (save ${(plan.price.monthly - plan.price.annual) * 12}/yr)
                        </p>
                      )}
                    </div>
                    <Button
                      className={`w-full ${plan.current ? "" : plan.popular ? "bg-primary hover:bg-primary/90" : ""}`}
                      variant={plan.current ? "outline" : "default"}
                      disabled={plan.current}
                      onClick={() => plan.price ? toast.info("Stripe checkout coming soon") : toast.info("Contact sales@odesie.com for Enterprise pricing")}
                      data-testid={`select-plan-${plan.name.toLowerCase()}`}
                    >
                      {plan.current ? "Current Plan" : plan.cta}
                      {!plan.current && plan.price && <ArrowUpRight className="w-3.5 h-3.5 ml-1" />}
                    </Button>
                    <div className="mt-5 space-y-2.5">
                      {plan.features.map((f, j) => (
                        <div key={j} className="flex items-center gap-2.5">
                          {f.included ? (
                            <div className="w-4 h-4 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                              <Check className="w-2.5 h-2.5 text-emerald-600" />
                            </div>
                          ) : (
                            <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <span className="text-muted-foreground/40 text-[8px]">--</span>
                            </div>
                          )}
                          <span className={`text-xs ${f.included ? "text-foreground" : "text-muted-foreground/60"}`}>{f.text}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Feature Comparison Table */}
            <Card className="border shadow-none">
              <CardHeader className="pt-5 pb-3 px-6">
                <CardTitle className="text-base">Detailed Feature Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-4 gap-0 text-sm font-semibold border-b bg-muted/30 px-6 py-3">
                  <span>Feature</span>
                  <span className="text-center">Starter</span>
                  <span className="text-center">Professional</span>
                  <span className="text-center">Enterprise</span>
                </div>
                {featureComparison.map((cat, ci) => (
                  <div key={ci}>
                    <div className="px-6 py-2.5 bg-muted/20 border-b">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{cat.category}</span>
                    </div>
                    {cat.features.map((f, fi) => (
                      <div key={fi} className="grid grid-cols-4 gap-0 px-6 py-2.5 border-b hover:bg-muted/10 transition-colors" data-testid={`compare-${f.name.toLowerCase().replace(/\s/g,'-')}`}>
                        <span className="text-xs font-medium">{f.name}</span>
                        <span className="text-center">{renderFeatureValue(f.starter)}</span>
                        <span className="text-center">{renderFeatureValue(f.professional)}</span>
                        <span className="text-center">{renderFeatureValue(f.enterprise)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Enterprise CTA */}
            <Card className="border shadow-none bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <CardContent className="p-6 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold">Need a custom solution?</h3>
                  <p className="text-xs text-white/70 mt-0.5">Get a personalized demo and custom pricing tailored to your manufacturing organization's needs.</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="text-xs h-9 bg-transparent border-white/30 text-white hover:bg-white/10">
                    <Headphones className="w-3.5 h-3.5 mr-1.5" /> Talk to Sales
                  </Button>
                  <Button size="sm" className="text-xs h-9 bg-white text-slate-900 hover:bg-white/90">
                    Book a Demo
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Frequently Asked Questions</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-3">
                {[
                  { q: "Can I change plans at any time?", a: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate your billing." },
                  { q: "What happens when I exceed my plan limits?", a: "You'll receive a notification at 80% and 100% usage. We won't cut off access, but you'll need to upgrade to continue adding resources." },
                  { q: "Do you offer volume discounts?", a: "Yes, for organizations with 500+ users. Contact our sales team for custom pricing." },
                  { q: "Is there a free trial?", a: "We offer a 14-day free trial of the Professional plan. No credit card required." },
                ].map((faq, i) => (
                  <div key={i} className="p-3 rounded-lg border">
                    <p className="text-sm font-medium">{faq.q}</p>
                    <p className="text-xs text-muted-foreground mt-1">{faq.a}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BillingPage;
